import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { WorkerError as WorkerErrorType } from '../types/worker.types';
import { Button, Card, CardContent } from '../components/ui';

interface WorkerErrorProps {
  error: WorkerErrorType;
  onRetry?: () => void;
}

export function WorkerError({ error, onRetry }: WorkerErrorProps) {
  const getErrorConfig = (errorType: WorkerErrorType['type']) => {
    switch (errorType) {
      case 'invalid_token':
        return {
          title: 'Token invalide',
          icon: AlertCircle,
          iconColor: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          showRetry: false
        };
      case 'project_inactive':
        return {
          title: 'Projet inactif',
          icon: AlertCircle,
          iconColor: 'text-orange-500',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          showRetry: false
        };
      case 'network_error':
        return {
          title: 'Erreur de connexion',
          icon: AlertCircle,
          iconColor: 'text-yellow-500',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          showRetry: true
        };
      case 'permission_denied':
        return {
          title: 'Accès refusé',
          icon: AlertCircle,
          iconColor: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          showRetry: false
        };
      default:
        return {
          title: 'Erreur inconnue',
          icon: AlertCircle,
          iconColor: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          showRetry: true
        };
    }
  };

  const config = getErrorConfig(error.type);
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <Card className={`${config.bgColor} ${config.borderColor} border-2`}>
          <CardContent className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center">
                <Icon className={`h-8 w-8 ${config.iconColor}`} />
              </div>
            </div>

            <div className="space-y-2">
              <h1 className="text-xl font-semibold text-gray-900">
                {config.title}
              </h1>
              <p className="text-gray-600">
                {error.message}
              </p>
              {error.details && (
                <p className="text-sm text-gray-500 mt-2">
                  {error.details}
                </p>
              )}
            </div>

            <div className="space-y-3">
              {config.showRetry && onRetry && (
                <Button
                  onClick={onRetry}
                  variant="primary"
                  fullWidth
                  icon={RefreshCw}
                >
                  Réessayer
                </Button>
              )}

              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
                fullWidth
                icon={Home}
              >
                Retour à l'accueil
              </Button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-400">
                Si le problème persiste, contactez l'administrateur
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}